/**
 * Industry Collaborations & MoUs — one institute-wide catalogue, shared by the
 * standalone /international page (renamed "Industry Collaborations & MoUs"), the
 * per-department section on the Department detail page, and the per-partner
 * detail page at /international/partners/:slug.
 *
 * Each entry is tagged with the department codes it is relevant to via `depts`.
 * Use the literal "ALL" for institute-wide tie-ups that should surface on every
 * department page. `getIndustryPartnersByDept(code)` returns the dept-specific
 * partners plus every "ALL" entry; `getIndustryPartner(slug)` powers the detail
 * page.
 */

export interface IndustryEngagement {
  kind: string;
  detail: string;
}

export interface IndustryFacultyLink {
  name: string;
  areas: string;
}

export interface IndustryActivity {
  year: string;
  what: string;
}

export interface IndustryPartner {
  slug: string;
  /** Company / body name — shown as the card and detail title. */
  name: string;
  /** Short name used in the context bar and meta card. */
  short: string;
  /** Short badge in the card's top-right corner, e.g. "IT", "AUTO". */
  tag: string;
  /** Sector label shown above the name, e.g. "Information Technology". */
  sector: string;
  /** One-line summary of what the collaboration covers. */
  desc: string;
  /** Footer line, e.g. "MoU since 2018". */
  since: string;
  /** Department codes this MoU serves, or ["ALL"] for institute-wide. */
  depts: string[];
  /** Lifecycle of the MoU. */
  status: "active" | "renewing" | "expanding";
  /** Headline metrics for the detail hero. */
  metrics: { interns: number; projects: number };
  /** About-the-partnership paragraphs on the detail page. */
  intro: string[];
  /** Engagement modes covered by the MoU. */
  engagements: IndustryEngagement[];
  /** BIET faculty who anchor the relationship. */
  faculty: IndustryFacultyLink[];
  /** Recent milestones. */
  recent: IndustryActivity[];
  /** Liaison contacts. */
  contact: { biet: string; partnerLiaison: string };
}

export const INDUSTRY_PARTNERS: IndustryPartner[] = [
  {
    slug: "infosys",
    name: "Infosys Ltd.",
    short: "Infosys",
    tag: "IT",
    sector: "Information Technology",
    desc: "Campus Connect curriculum alignment, faculty enablement and a sustained intern-to-hire pipeline across software engineering programs.",
    since: "MoU since 2016",
    depts: ["CSE", "ISE", "AIML", "MCA"],
    status: "active",
    metrics: { interns: 180, projects: 12 },
    intro: [
      "Infosys is one of BIET's longest-standing IT partners. The Campus Connect MoU — signed in 2016 and renewed in 2022 — aligns elective content with industry practice, certifies faculty on the Infosys foundation curriculum, and runs a structured internship-to-placement pipeline for pre-final-year students.",
      "The collaboration is anchored by the InfyTQ platform and an annual on-campus foundation program that has prepared over 1,200 BIET students for industry roles since 2017.",
    ],
    engagements: [
      { kind: "Campus Connect", detail: "Curriculum alignment and soft-skills modules embedded into the 5th–6th semester electives" },
      { kind: "Faculty enablement", detail: "InfyTQ train-the-trainer certification for core CS faculty, twice a year" },
      { kind: "Intern-to-hire", detail: "8–10 week internships for pre-final-year students with conversion to placement offers" },
    ],
    faculty: [
      { name: "Dr. Nirmala C R", areas: "Single point of contact · CSE" },
      { name: "Prof. Faculty-12", areas: "Campus Connect coordinator" },
    ],
    recent: [
      { year: "2024", what: "Renewed MoU expanded to cover the AI & ML program with applied GenAI modules." },
      { year: "2023", what: "146 students completed the Infosys foundation program; 71 received internship offers." },
      { year: "2021", what: "InfyTQ certification rolled out to 18 BIET faculty across CSE and ISE." },
    ],
    contact: { biet: "Dr. Nirmala C R, Head — CSE", partnerLiaison: "Infosys Campus Connect, Mysuru DC" },
  },
  {
    slug: "tcs",
    name: "Tata Consultancy Services",
    short: "TCS",
    tag: "IT",
    sector: "Information Technology",
    desc: "Co-academia programs, the TCS iON assessment platform for placements, and joint capstone projects in enterprise systems.",
    since: "MoU since 2014",
    depts: ["CSE", "ISE", "MCA"],
    status: "active",
    metrics: { interns: 150, projects: 9 },
    intro: [
      "The TCS partnership is BIET's oldest IT-industry tie-up, dating to 2014. It combines the TCS Co-Academia curriculum, the iON assessment and recruitment platform, and a steady stream of National Qualifier Test placements.",
      "Joint capstone projects in enterprise systems and cloud migration give final-year students exposure to production-scale problems mentored by TCS engineers.",
    ],
    engagements: [
      { kind: "Co-Academia", detail: "Industry-aligned modules and a recognised soft-skills curriculum" },
      { kind: "TCS iON / NQT", detail: "Assessment platform and National Qualifier Test administered on campus" },
      { kind: "Capstone mentoring", detail: "Enterprise-systems and cloud projects co-mentored by TCS staff" },
    ],
    faculty: [
      { name: "Prof. Faculty-08", areas: "Placement & co-academia · ISE" },
      { name: "Prof. Faculty-21", areas: "Capstone coordination · CSE" },
    ],
    recent: [
      { year: "2024", what: "Largest single-recruiter cohort — 58 offers via the TCS NQT." },
      { year: "2022", what: "Co-Academia curriculum refreshed with cloud-native and DevOps content." },
    ],
    contact: { biet: "Prof. Faculty-08, Placement Cell", partnerLiaison: "TCS Academic Interface, Bengaluru" },
  },
  {
    slug: "wipro",
    name: "Wipro Ltd.",
    short: "Wipro",
    tag: "IT",
    sector: "Information Technology",
    desc: "Wipro TalentNext faculty development, cloud and full-stack electives, and a dedicated recruitment track for pre-final-year students.",
    since: "MoU since 2017",
    depts: ["CSE", "ISE", "AIML"],
    status: "active",
    metrics: { interns: 120, projects: 7 },
    intro: [
      "Wipro's TalentNext program underpins this MoU, focusing on faculty development and curriculum currency in cloud and full-stack engineering.",
      "A dedicated recruitment track and project-based learning labs keep the collaboration close to the day-to-day of a delivery organisation.",
    ],
    engagements: [
      { kind: "TalentNext", detail: "Faculty development workshops and a recognised industry curriculum" },
      { kind: "Skill electives", detail: "Cloud and full-stack electives delivered with Wipro courseware" },
      { kind: "Recruitment track", detail: "Dedicated hiring drive for eligible CSE/ISE/AIML students" },
    ],
    faculty: [
      { name: "Prof. Faculty-14", areas: "TalentNext lead · CSE" },
      { name: "Prof. Faculty-30", areas: "Full-stack lab · ISE" },
    ],
    recent: [
      { year: "2023", what: "Full-stack project lab equipped under the TalentNext initiative." },
      { year: "2021", what: "First Wipro WILP cohort onboarded from BIET graduates." },
    ],
    contact: { biet: "Prof. Faculty-14, CSE", partnerLiaison: "Wipro TalentNext, Bengaluru" },
  },
  {
    slug: "microsoft-edunet",
    name: "Microsoft (via Edunet)",
    short: "Microsoft",
    tag: "CLOUD",
    sector: "Cloud & AI",
    desc: "Azure and AI skilling labs, certification vouchers and a Centre of Excellence for applied machine-learning projects.",
    since: "MoU since 2021",
    depts: ["CSE", "AIML", "ISE"],
    status: "expanding",
    metrics: { interns: 90, projects: 10 },
    intro: [
      "Delivered through the Edunet Foundation, this collaboration brings Microsoft's Azure and AI curriculum to campus, with certification vouchers and a Centre of Excellence focused on applied machine learning.",
      "The CoE hosts a rolling set of student projects in computer vision and natural-language processing, several of which have progressed to national hackathon finals.",
    ],
    engagements: [
      { kind: "Azure / AI labs", detail: "Hands-on skilling tracks with Microsoft Learn content" },
      { kind: "Certification", detail: "Subsidised vouchers for AI-900, AZ-900 and DP-900" },
      { kind: "CoE projects", detail: "Applied ML capstones mentored within the Centre of Excellence" },
    ],
    faculty: [
      { name: "Prof. Faculty-05", areas: "CoE lead · AIML" },
      { name: "Prof. Faculty-26", areas: "Cloud skilling · CSE" },
    ],
    recent: [
      { year: "2024", what: "CoE expanded with a GenAI track; two student teams reached national finals." },
      { year: "2022", what: "First cohort of 120 students cleared Azure fundamentals certification." },
    ],
    contact: { biet: "Prof. Faculty-05, AIML", partnerLiaison: "Edunet Foundation (Microsoft)" },
  },
  {
    slug: "bosch",
    name: "Robert Bosch India",
    short: "Bosch",
    tag: "AUTO",
    sector: "Automotive Electronics",
    desc: "Sponsored embedded-systems lab, BoschTECH guest lectures and live problem statements in mechatronics and EV powertrains.",
    since: "MoU since 2018",
    depts: ["ECE", "EEE", "MECH"],
    status: "active",
    metrics: { interns: 70, projects: 14 },
    intro: [
      "Bosch sponsors a dedicated embedded-systems laboratory at BIET and seeds live problem statements in mechatronics, automotive electronics and EV powertrains.",
      "The BoschTECH lecture series and a structured internship at the Bengaluru and Coimbatore facilities keep students close to current automotive practice.",
    ],
    engagements: [
      { kind: "Sponsored lab", detail: "Bosch embedded-systems lab with current automotive toolchains" },
      { kind: "BoschTECH series", detail: "Guest lectures on EV powertrains, sensors and functional safety" },
      { kind: "Live projects", detail: "Industry-defined problem statements for final-year teams" },
    ],
    faculty: [
      { name: "Prof. Faculty-09", areas: "Embedded lab in-charge · ECE" },
      { name: "Prof. Faculty-18", areas: "Mechatronics projects · MECH" },
    ],
    recent: [
      { year: "2024", what: "Two BIET teams shortlisted in the Bosch India mobility challenge." },
      { year: "2021", what: "Embedded-systems lab upgraded with EV powertrain instrumentation." },
    ],
    contact: { biet: "Prof. Faculty-09, ECE", partnerLiaison: "Robert Bosch, Bengaluru" },
  },
  {
    slug: "siemens",
    name: "Siemens Ltd.",
    short: "Siemens",
    tag: "AUTO",
    sector: "Industrial Automation",
    desc: "Centre of Excellence in industrial automation, PLC/SCADA training and certification on Siemens TIA Portal and drives.",
    since: "CoE since 2019",
    depts: ["EEE", "ECE", "MECH"],
    status: "expanding",
    metrics: { interns: 60, projects: 8 },
    intro: [
      "The Siemens Centre of Excellence in industrial automation is a flagship facility at BIET, equipped with PLC, SCADA and drive systems used across modern manufacturing.",
      "Students train and certify on the Siemens TIA Portal, and the CoE doubles as a short-term skilling centre for working professionals in the region.",
    ],
    engagements: [
      { kind: "Centre of Excellence", detail: "Automation hardware — PLC, SCADA, HMI and variable-frequency drives" },
      { kind: "TIA Portal certification", detail: "Recognised certification tracks for students and faculty" },
      { kind: "Regional skilling", detail: "Short courses for industry professionals run from the CoE" },
    ],
    faculty: [
      { name: "Prof. Faculty-11", areas: "CoE coordinator · EEE" },
      { name: "Prof. Faculty-23", areas: "Automation projects · MECH" },
    ],
    recent: [
      { year: "2023", what: "CoE certified 90 students on TIA Portal; added a motion-control rig." },
      { year: "2019", what: "Siemens CoE inaugurated with industrial automation hardware." },
    ],
    contact: { biet: "Prof. Faculty-11, EEE", partnerLiaison: "Siemens Digital Industries" },
  },
  {
    slug: "bel",
    name: "Bharat Electronics (BEL)",
    short: "BEL",
    tag: "DEF",
    sector: "Defence Electronics",
    desc: "Joint projects in RF, radar and communication systems, internships at the Bengaluru complex and final-year project mentorship.",
    since: "MoU since 2015",
    depts: ["ECE", "TC"],
    status: "active",
    metrics: { interns: 45, projects: 6 },
    intro: [
      "This MoU with the public-sector defence major Bharat Electronics opens up joint work in RF, radar and secure communication systems.",
      "Select students intern at the BEL Bengaluru complex, and engineers from BEL mentor final-year projects with a defence-electronics focus.",
    ],
    engagements: [
      { kind: "Joint projects", detail: "RF, radar and communication-systems problem statements" },
      { kind: "Internships", detail: "Vetted internships at the BEL Bengaluru complex" },
      { kind: "Project mentorship", detail: "BEL engineers co-guide final-year ECE/TC projects" },
    ],
    faculty: [
      { name: "Prof. Faculty-17", areas: "RF & communications · ECE" },
      { name: "Prof. Faculty-28", areas: "Signal processing · TC" },
    ],
    recent: [
      { year: "2023", what: "Joint paper on radar signal processing presented at a national symposium." },
      { year: "2020", what: "Six students completed defence-electronics internships at BEL." },
    ],
    contact: { biet: "Prof. Faculty-17, ECE", partnerLiaison: "BEL Bengaluru, HR & Academia" },
  },
  {
    slug: "toyota-kirloskar",
    name: "Toyota Kirloskar Motor",
    short: "Toyota",
    tag: "MFG",
    sector: "Manufacturing",
    desc: "Lean-manufacturing and TPM workshops, plant immersion programs and sponsored projects in production and quality engineering.",
    since: "MoU since 2018",
    depts: ["MECH", "IPE"],
    status: "active",
    metrics: { interns: 55, projects: 7 },
    intro: [
      "Toyota Kirloskar Motor brings the Toyota Production System into BIET's mechanical and production curriculum through lean-manufacturing and TPM workshops.",
      "Plant immersion programs at the Bidadi facility give students first-hand exposure to world-class manufacturing discipline.",
    ],
    engagements: [
      { kind: "Lean / TPM workshops", detail: "Toyota Production System and Total Productive Maintenance modules" },
      { kind: "Plant immersion", detail: "Structured visits and short residencies at the Bidadi plant" },
      { kind: "Sponsored projects", detail: "Production and quality-engineering problem statements" },
    ],
    faculty: [
      { name: "Prof. Faculty-18", areas: "Manufacturing systems · MECH" },
      { name: "Prof. Faculty-31", areas: "Quality & operations · IPE" },
    ],
    recent: [
      { year: "2024", what: "Kaizen project from a BIET team adopted as a pilot at a vendor unit." },
      { year: "2019", what: "First TPM workshop series delivered to 60 mechanical students." },
    ],
    contact: { biet: "Prof. Faculty-18, MECH", partnerLiaison: "Toyota Kirloskar, Bidadi" },
  },
  {
    slug: "lt-construction",
    name: "L&T Construction",
    short: "L&T",
    tag: "INFRA",
    sector: "Infrastructure",
    desc: "Site-exposure programs, BIM and construction-management electives and a structured intake into the L&T Construction Skills track.",
    since: "MoU since 2016",
    depts: ["CIVIL"],
    status: "renewing",
    metrics: { interns: 50, projects: 5 },
    intro: [
      "L&T Construction anchors BIET's civil-engineering industry connect, with site-exposure programs across active infrastructure projects in Karnataka.",
      "BIM and construction-management electives, co-developed with L&T, feed a structured intake into the L&T Construction Skills Training Institute.",
    ],
    engagements: [
      { kind: "Site exposure", detail: "Supervised visits and internships at live infrastructure sites" },
      { kind: "BIM electives", detail: "Building Information Modelling and construction-management modules" },
      { kind: "Skills track", detail: "Structured intake into the L&T CSTI placement pipeline" },
    ],
    faculty: [
      { name: "Prof. Faculty-07", areas: "Construction management · CIVIL" },
      { name: "Prof. Faculty-19", areas: "Structural & BIM · CIVIL" },
    ],
    recent: [
      { year: "2024", what: "MoU under renewal to add a precast-technology module." },
      { year: "2022", what: "BIM elective launched; 40 students completed the first cohort." },
    ],
    contact: { biet: "Prof. Faculty-07, CIVIL", partnerLiaison: "L&T CSTI, Kanchipuram" },
  },
  {
    slug: "ultratech",
    name: "UltraTech Cement",
    short: "UltraTech",
    tag: "CORE",
    sector: "Construction Materials",
    desc: "Concrete-technology lab support, materials-testing collaboration and guest sessions on sustainable construction practice.",
    since: "MoU since 2019",
    depts: ["CIVIL", "MECH"],
    status: "active",
    metrics: { interns: 30, projects: 4 },
    intro: [
      "UltraTech Cement supports BIET's concrete-technology laboratory and collaborates on materials-testing research relevant to regional construction.",
      "Guest sessions on sustainable construction and supplementary cementitious materials connect classroom learning to industry practice.",
    ],
    engagements: [
      { kind: "Lab support", detail: "Equipment and consumables for the concrete-technology lab" },
      { kind: "Materials testing", detail: "Joint testing of mixes and supplementary cementitious materials" },
      { kind: "Guest sessions", detail: "Sustainable-construction and durability lectures" },
    ],
    faculty: [
      { name: "Prof. Faculty-19", areas: "Concrete technology · CIVIL" },
      { name: "Prof. Faculty-22", areas: "Materials · MECH" },
    ],
    recent: [
      { year: "2023", what: "Joint study on fly-ash blended mixes for low-cost housing." },
      { year: "2019", what: "Concrete-technology lab upgraded with UltraTech support." },
    ],
    contact: { biet: "Prof. Faculty-19, CIVIL", partnerLiaison: "UltraTech Cement, Technical Services" },
  },
  {
    slug: "kpcl",
    name: "Karnataka Power Corp. (KPCL)",
    short: "KPCL",
    tag: "POWER",
    sector: "Power & Energy",
    desc: "Industrial visits to thermal and hydel stations, internships in power-system operations and projects on grid stability.",
    since: "MoU since 2017",
    depts: ["EEE"],
    status: "active",
    metrics: { interns: 40, projects: 5 },
    intro: [
      "The KPCL MoU gives electrical-engineering students access to thermal and hydel generating stations across Karnataka for industrial visits and internships.",
      "Joint projects focus on power-system operations, protection and grid stability — areas where regional utilities need fresh talent.",
    ],
    engagements: [
      { kind: "Station visits", detail: "Supervised visits to thermal and hydel generating stations" },
      { kind: "Operations internships", detail: "Internships in power-system operation and maintenance" },
      { kind: "Grid projects", detail: "Final-year projects on protection and grid stability" },
    ],
    faculty: [
      { name: "Prof. Faculty-11", areas: "Power systems · EEE" },
      { name: "Prof. Faculty-25", areas: "Protection & control · EEE" },
    ],
    recent: [
      { year: "2023", what: "20 students completed operations internships at KPCL stations." },
      { year: "2018", what: "First industrial visit program to the Raichur thermal station." },
    ],
    contact: { biet: "Prof. Faculty-11, EEE", partnerLiaison: "KPCL Training & HRD" },
  },
  {
    slug: "quest-global",
    name: "Quest Global",
    short: "Quest",
    tag: "ENGG",
    sector: "Engineering Services",
    desc: "Industry-defined capstone projects in CAD/CAE and embedded design, with a dedicated engineering-services recruitment drive.",
    since: "MoU since 2020",
    depts: ["MECH", "ECE", "IPE"],
    status: "active",
    metrics: { interns: 48, projects: 9 },
    intro: [
      "Quest Global, a global engineering-services company, seeds industry-defined capstone projects in CAD/CAE, embedded design and product engineering.",
      "A dedicated recruitment drive places students into engineering-services roles spanning aerospace, automotive and industrial domains.",
    ],
    engagements: [
      { kind: "Capstone projects", detail: "CAD/CAE and embedded-design problem statements" },
      { kind: "Skill workshops", detail: "Product-engineering and simulation tool training" },
      { kind: "Recruitment", detail: "Engineering-services hiring across MECH/ECE/IPE" },
    ],
    faculty: [
      { name: "Prof. Faculty-20", areas: "Design & CAE · MECH" },
      { name: "Prof. Faculty-27", areas: "Embedded design · ECE" },
    ],
    recent: [
      { year: "2024", what: "Nine capstone teams worked on Quest-defined product problems." },
      { year: "2021", what: "First recruitment drive placed 16 students into engineering-services roles." },
    ],
    contact: { biet: "Prof. Faculty-20, MECH", partnerLiaison: "Quest Global, Bengaluru" },
  },
  {
    slug: "cii",
    name: "Confederation of Indian Industry",
    short: "CII",
    tag: "BODY",
    sector: "Industry Body",
    desc: "Institute-wide industry-academia council, the annual industry conclave and CII-mediated internship and mentorship drives.",
    since: "MoU since 2013",
    depts: ["ALL"],
    status: "active",
    metrics: { interns: 200, projects: 18 },
    intro: [
      "As an institute-wide member of the Confederation of Indian Industry, BIET runs a standing industry-academia council that shapes curriculum, projects and placements across all departments.",
      "The annual BIET–CII industry conclave brings dozens of regional and national firms to campus, and CII mediates internship and mentorship drives throughout the year.",
    ],
    engagements: [
      { kind: "Industry council", detail: "Cross-department advisory board on curriculum and skilling" },
      { kind: "Annual conclave", detail: "On-campus industry conclave with 30+ participating firms" },
      { kind: "Internship drives", detail: "CII-mediated internships and mentor matching across branches" },
    ],
    faculty: [
      { name: "Dr. Faculty-15", areas: "Dean — Industry Relations" },
      { name: "Prof. Faculty-03", areas: "Industry council secretary" },
    ],
    recent: [
      { year: "2024", what: "Industry conclave hosted 34 firms; 210 internship slots opened." },
      { year: "2016", what: "Standing industry-academia council formalised across all departments." },
    ],
    contact: { biet: "Dr. Faculty-15, Dean (Industry Relations)", partnerLiaison: "CII Karnataka State Council" },
  },
  {
    slug: "nasscom-futureskills",
    name: "NASSCOM FutureSkills",
    short: "NASSCOM",
    tag: "SKILL",
    sector: "Skilling & Certification",
    desc: "Access to the FutureSkills Prime platform, emerging-tech micro-credentials and faculty train-the-trainer programs across branches.",
    since: "MoU since 2020",
    depts: ["ALL"],
    status: "expanding",
    metrics: { interns: 120, projects: 6 },
    intro: [
      "Through NASSCOM FutureSkills, BIET students and faculty access the FutureSkills Prime platform and earn emerging-technology micro-credentials recognised across the IT industry.",
      "Train-the-trainer programs keep faculty current on AI, cloud, cybersecurity and data engineering, benefiting every branch of the institute.",
    ],
    engagements: [
      { kind: "FutureSkills Prime", detail: "Institute-wide access to the national skilling platform" },
      { kind: "Micro-credentials", detail: "Emerging-tech badges in AI, cloud, data and cybersecurity" },
      { kind: "Train-the-trainer", detail: "Faculty enablement programs delivered across departments" },
    ],
    faculty: [
      { name: "Prof. Faculty-03", areas: "Skilling coordinator" },
      { name: "Prof. Faculty-05", areas: "Emerging tech · AIML" },
    ],
    recent: [
      { year: "2024", what: "Over 600 students enrolled on FutureSkills Prime micro-credential tracks." },
      { year: "2021", what: "First institute-wide train-the-trainer cohort completed." },
    ],
    contact: { biet: "Prof. Faculty-03, Skilling Cell", partnerLiaison: "NASSCOM FutureSkills" },
  },
];

/**
 * Partners relevant to a department: those that name the department code plus
 * every institute-wide ("ALL") tie-up. Dept-specific entries are listed first.
 */
export function getIndustryPartnersByDept(code: string): IndustryPartner[] {
  const dept = INDUSTRY_PARTNERS.filter((p) => p.depts.includes(code));
  const wide = INDUSTRY_PARTNERS.filter((p) => p.depts.includes("ALL"));
  return [...dept, ...wide];
}

/** Look up a single partner for the detail page. */
export function getIndustryPartner(slug: string): IndustryPartner | undefined {
  return INDUSTRY_PARTNERS.find((p) => p.slug === slug);
}
